import {
  faDeleteLeft,
  type IconDefinition
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

import { priceFormatter } from "~/utils/price-formatter";
import { useT } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { type Currency } from "~/api/products/types";
import { DEFAULT_CURRENCY } from "~/utils/constants";
import { cn } from "~/lib/utils";

import { calculateValue } from "./calculateValue";

type NumPadVariant = "currency" | "percentage" | "amount";
type NumPadLayout = "vertical" | "horizontal";

const calculatorButtons: ("00" | number | IconDefinition)[] = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  "00",
  0,
  faDeleteLeft
];

export type NumPadProps = {
  variant?: NumPadVariant;
  layout?: NumPadLayout;
  value: number;
  setValue: (value: number) => void;
  placeholder?: number;
  children?: React.ReactNode;
  label?: string;
  maximum?: number;
  currency?: Currency;
};

export const NumPad = ({
  variant = "currency",
  layout = "vertical",
  value,
  setValue,
  placeholder = 0,
  children,
  label,
  maximum = Infinity,
  currency = DEFAULT_CURRENCY
}: NumPadProps) => {
  const [error, setError] = useState<string | null>(null);

  const t = useT();

  const { locale } = useCurrentTillLocalisation();

  return (
    <div
      className={cn("flex w-full", {
        "flex-col px-11 py-8": layout === "vertical",
        "flex-row px-5 pt-5": layout === "horizontal"
      })}
    >
      <div className="mb-8 flex w-full flex-col">
        {label ? (
          <p
            className={cn("text-text-disabled", {
              "text-base": layout === "vertical",
              "text-3xl": layout === "horizontal"
            })}
          >
            {label}
          </p>
        ) : null}
        <p
          className={cn(
            "col-span-2 font-medium",
            {
              "text-5xl text-center": layout === "vertical",
              "text-7xl": layout === "horizontal"
            },
            value > 0 ? "text-text-primary" : "text-text-disabled"
          )}
        >
          {variant === "currency" &&
            priceFormatter({
              value: (value || placeholder) * 100,
              currency,
              locale,
              options: {
                showDecimals: true,
                hideCurrency: true
              }
            })}
          {variant === "percentage" && `${value}%`}
          {variant === "amount" && value}
          {variant === "amount" && error ? (
            <p className="text-lg text-danger-main">{error}</p>
          ) : null}
        </p>

        {variant === "currency" && (
          <p
            className={cn("uppercase text-text-disabled", {
              "text-3xl text-center": layout === "vertical",
              "text-5xl": layout === "horizontal"
            })}
          >
            {currency}
          </p>
        )}
      </div>

      <div className="m-auto grid w-full grid-cols-3 grid-rows-4 gap-3">
        {calculatorButtons.map((input) => (
          <SquareButton
            className={cn({ "!text-text-disabled": input === "00" })}
            key={`key-${typeof input === "object" ? input.iconName : input}`}
            onClick={() => {
              const inputType = typeof input === "object" ? "cancel" : input;
              const newValue = calculateValue(value, inputType, variant);

              if (newValue > maximum) {
                setError(t("Maximum value is {maximum}", { maximum }));
                return;
              }

              setError(null);

              setValue(newValue);
            }}
          >
            {typeof input === "object" ? (
              <div className="flex size-full items-center justify-center">
                <FontAwesomeIcon icon={input} size="sm" />
              </div>
            ) : (
              input
            )}
          </SquareButton>
        ))}
        {children}
      </div>
    </div>
  );
};

const SquareButton = ({
  className,
  ...rest
}: JSX.IntrinsicElements["button"]) => {
  return (
    <button
      className={cn(
        "flex h-16 items-center justify-center rounded-md border border-text-primary text-5xl text-text-primary lg:h-20 xl:h-24",
        className
      )}
      type="button"
      {...rest}
    />
  );
};
